import { getSuggestedDraftTexts } from 'owa-copilot-suggested-drafts-store/lib/selectors/getSuggestedDraftTexts';
import {
    type Suggestion,
    type Suggestions,
    composeNewHelpWrite,
    composeNewFindRecipients,
    composeNewSupportStatement,
    composeReplyHelpReply,
    composeReplyNextSteps,
    composeReplyKeyPoints,
    composeForwardExplain,
    composeForwardWithContext,
    composeForwardWithTasks,
} from './suggestionCatalog';
import { executePromptWithQueryAnnotation } from './executePromptWithQueryAnnotation';
import loc from 'owa-localize';
import type { MailboxInfo } from 'owa-client-types';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { type ComposeOperation } from 'owa-mail-compose-store/lib/store/schema/ComposeOperation';
import { replyPromptText, draftReply } from './getComposingSuggestions.locstring.json';
import { SUA_REPLY_EXPERIMENT_CMM_ID } from 'owa-bizchat-shared/lib/utils/ScenarioCMMIds';
import type { ComposeViewState } from 'owa-mail-compose-store';
import { isDraftAssistantEnabled } from 'owa-copilot-settings-utils/lib/utils/isDraftAssistantEnabled';

export function getComposingSuggestions(composeViewState?: ComposeViewState): Suggestions {
    if (!composeViewState) {
        return undefined;
    }

    if (!isGlobalFeatureEnabled('copilot-dab-mail-compose-sua')) {
        return undefined;
    }

    const suggestions = getComposeScenarioSuggestions(composeViewState.operation);

    // When the draft reply experiment is enabled in reply/reply-all,
    // replace the 2nd and 3rd scenario suggestions with generated draft replies.
    const draftReplies = getExperimentDraftReplies(composeViewState);
    if (draftReplies && suggestions) {
        return [suggestions[0], draftReplies[0], draftReplies[1]];
    }

    return suggestions;
}

/**
 * Returns draft reply suggestions when the draft reply experiment is enabled
 * and the user is composing a reply/reply-all. Used by both compose-sua and 3S paths.
 */
export function getExperimentDraftReplies(
    composeViewState?: ComposeViewState
): Suggestions | undefined {
    if (!isGlobalFeatureEnabled('mon-copilot-dab-experiment-draft-reply')) {
        return undefined;
    }

    if (!composeViewState) {
        return undefined;
    }

    if (composeViewState.operation !== 1 && composeViewState.operation !== 2) {
        return undefined;
    }

    return getDraftReplySuggestions(composeViewState);
}

function getDraftReplySuggestions(composeViewState: {
    conversationId?: string;
    referenceItemId?: {
        Id?: string;
    } | null;
}): Suggestions | undefined {
    const { conversationId, referenceItemId } = composeViewState;

    const suggestedDraftTexts = getSuggestedDraftTexts(
        conversationId ?? '',
        referenceItemId?.Id ?? '',
        'Outlook'
    );

    if (
        !suggestedDraftTexts ||
        suggestedDraftTexts.length < 2 ||
        suggestedDraftTexts.slice(0, 2).find(text => !text) !== undefined
    ) {
        return undefined;
    }

    return [
        getDraftSuggestion('suggestedDraft1', suggestedDraftTexts[0]),
        getDraftSuggestion('suggestedDraft2', suggestedDraftTexts[1]),
    ];
}

const getDraftSuggestion = (id: string, text: string): Suggestion => {
    return {
        displayTextLocalized: loc(draftReply, text),
        id,
        onClick: (targetWindow: Window, mailboxInfo: MailboxInfo) => {
            executePromptWithQueryAnnotation(
                loc(replyPromptText, text),
                SUA_REPLY_EXPERIMENT_CMM_ID,
                targetWindow,
                mailboxInfo,
                undefined,
                isDraftAssistantEnabled(mailboxInfo) ? 'DraftAssistant' : undefined
            );
        },
    };
};

function getComposeScenarioSuggestions(operation?: ComposeOperation): Suggestions {
    switch (operation) {
        case 3:
            return [composeForwardExplain, composeForwardWithContext, composeForwardWithTasks];
        case 1:
        case 2:
            return [composeReplyHelpReply, composeReplyNextSteps, composeReplyKeyPoints];
        default:
            return [composeNewHelpWrite, composeNewFindRecipients, composeNewSupportStatement];
    }
}
